import { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import {
    ProcessCellForExportParams,
    ShouldRowBeSkippedParams,
} from 'ag-grid-community'
import useIsMounted from 'hooks/utils/useIsMounted'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'
import {
    getClientMockData,
    getChargebacksMockData,
    getInvalidChargebacksMockData,
    getWebhooksMockData,
    updateAccountNumbersData,
    getAccountNumbersMockData,
    getReconciliationMockData,
    getRetrievalsMockData,
    getReconLogsFilesMockData,
    getReconLogsApprovalsMockData,
} from '../../utils/clientMockData'

interface AGDataGridActionsProps {
    data: any
    dataSource: any
    isLoading: boolean
    pageSize: number
    page: number
    paginationTotal: number
    setLocalPage: (page: number) => void
    handlePageLimitChange: (pageSize: number) => void
    handleSearchField: (value: string) => void
    currentPayload: { [key: string]: any } | undefined
    refreshAGGrid: () => void
    csvContextMenuItem: (fileName: string) => void
    excelContextMenuItem: (fileName: string) => void
    resetDataGridContextMenuItem: (gridName: string) => void
}

export const useAGDataGridActions = (
    url: string,
    gridRef: any,
    activeNodeId: number | undefined,
    requestPayload: { [key: string]: any },
    {
        defaultLimit = 25,
        excludedExportColumns = ['select', 'actions'],
        handleFormatExportCells = undefined,
        handleSkipExportRow = undefined,
        gridName = undefined,
        showClientTypeIssuerUI = false,
    }: {
        defaultLimit?: number
        excludedExportColumns?: string[]
        handleFormatExportCells?: (params: ProcessCellForExportParams) => void
        handleSkipExportRow?: (params: ShouldRowBeSkippedParams) => void
        gridName?: string
        showClientTypeIssuerUI?: boolean
    }
): AGDataGridActionsProps => {
    const { isMounted } = useIsMounted()
    const { themeVariantObj } = useThemeStyling()

    const [data, setData] = useState<{ [key: string]: any }>([])
    const [paginationTotal, setPaginationTotal] = useState<number>(0)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [localSearchValue, setLocalSearchValue] = useState<string>('')
    const [localPage, setLocalPage] = useState<number>(1)
    const [localPageSize, setLocalPageSize] = useState<number>(defaultLimit)
    const [localCurrentPayload, setLocalCurrentPayload] = useState(
        requestPayload
    )
    const localActiveNodeId = useRef(activeNodeId)
    const currentPayload = useRef(localCurrentPayload)
    const searchValue = useRef(localSearchValue)
    const pageSize = useRef(localPageSize)
    const page = useRef(1)

    const getMockData = (filterGroup: string) => {
        console.log('getMockData called with filterGroup:', filterGroup)

        switch (filterGroup) {
            case 'total_actions':
                return getClientMockData(
                    themeVariantObj,
                    'New',
                    'total_actions'
                )
            case 'issuer_docs':
                return getClientMockData(themeVariantObj, 'New', 'issuer_docs')
            case 'data_issue':
                return getClientMockData(themeVariantObj, 'New', 'data_issue')
            case 'merchant_response':
                return getClientMockData(
                    themeVariantObj,
                    'New',
                    'merchant_response'
                )
            case 'ai_docs':
                return getClientMockData(themeVariantObj, 'New', 'ai_docs')
            case 'purchase_inquiry':
                return getClientMockData(
                    themeVariantObj,
                    'InProgress',
                    'purchase_inquiry'
                )
            case 'merchant_inquiry':
                return getClientMockData(
                    themeVariantObj,
                    'InProgress',
                    'merchant_inquiry'
                )
            case 'chargeback':
                return getClientMockData(
                    themeVariantObj,
                    'InProgress',
                    'chargeback'
                )
            case 'prearb':
                return getClientMockData(
                    themeVariantObj,
                    'InProgress',
                    'prearb'
                )
            case 'in_progress_arbitrations':
                return getClientMockData(
                    themeVariantObj,
                    'InProgress',
                    'in_progress_arbitrations'
                )
            case 'split_request':
                return getClientMockData(
                    themeVariantObj,
                    'InProgress',
                    'split_request'
                )
            case 'total_closed':
                return getClientMockData(
                    themeVariantObj,
                    'Completed',
                    'total_closed'
                )
            case 'issuer_expired':
                return getClientMockData(
                    themeVariantObj,
                    'Completed',
                    'issuer_expired'
                )
            case 'issuer_accepted':
                return getClientMockData(
                    themeVariantObj,
                    'Completed',
                    'issuer_accepted'
                )
            case 'merchant_expired':
                return getClientMockData(
                    themeVariantObj,
                    'Completed',
                    'merchant_expired'
                )
            case 'merchant_accepted':
                return getClientMockData(
                    themeVariantObj,
                    'Completed',
                    'merchant_accepted'
                )
            case 'inquiries':
                return getClientMockData(
                    themeVariantObj,
                    'Completed',
                    'inquiries'
                )
            case 'completed_arbitrations':
                return getClientMockData(
                    themeVariantObj,
                    'Completed',
                    'completed_arbitrations'
                )
            case 'new':
                return getClientMockData(themeVariantObj, 'Draft', 'new')
            case 'draft':
                return getClientMockData(themeVariantObj, 'Draft', 'draft')
            case 'archived':
                return getClientMockData(themeVariantObj, 'Draft', 'archived')
            case 'chargebacks':
                return getChargebacksMockData(themeVariantObj)
            case 'reconciliation':
                return getReconciliationMockData(themeVariantObj)
            case 'retrievals':
                return getRetrievalsMockData(themeVariantObj)
            case 'recon-logs-files':
                return getReconLogsFilesMockData()
            case 'recon-logs-approvals':
                return getReconLogsApprovalsMockData()
            case 'invalidChargebacks':
                return getInvalidChargebacksMockData()
            case 'webhooksCaseStatus':
                return getWebhooksMockData('caseStatus')
            case 'webhooksVerdictStatus':
                return getWebhooksMockData('verdictStatus')
            case 'accountNumberMock':
                return getAccountNumbersMockData(
                    activeNodeId ?? 0,
                    themeVariantObj
                )
            default:
                return { data: [] }
        }
    }

    const updateData = (data: any, gridName?: string) => {
        switch (gridName) {
            case 'accountNumber':
                return updateAccountNumbersData(data)

            default:
                return data
        }
    }

    const dataSource = {
        getRows(params: any) {
            setIsLoading(true)
            let payload: any = {
                limit: pageSize.current,
                start: pageSize.current * page.current - pageSize.current,
                sort_order: 'asc',
                search: searchValue.current ? searchValue.current : undefined,
                ...currentPayload.current,
                ...(showClientTypeIssuerUI &&
                    gridName === 'case-mgmt' && {
                        filter_group: 'new_cases',
                    }),
            }
            const { filterModel, sortModel } = params.request

            //Sorting
            if (sortModel.length) {
                const { colId, sort } = sortModel[0]
                payload = {
                    ...payload,
                    sort_by: colId,
                    sort_order: sort,
                }
            }

            //Filtering
            const filterKeys = Object.keys(filterModel)
            filterKeys.forEach((filter) => {
                payload = {
                    ...payload,
                    [filter]: filterModel[filter].filter,
                }
            })

            // Check if this grid uses mock data — skip the API call entirely
            const isMockGrid =
                ([
                    'case-mgmt',
                    'chargebacks',
                    'invalidChargebacks',
                    'webhooksCaseStatus',
                    'webhooksVerdictStatus',
                    'accountNumberMock'
                ].includes(gridName ?? '') &&
                showClientTypeIssuerUI) ||
                gridName === 'reconciliation' ||
                gridName === 'retrievals' ||
                gridName === 'recon-logs-files' ||
                gridName === 'recon-logs-approvals'

            if (isMockGrid) {
                const filterGroup =
                    currentPayload.current?.filter_group || ''

                const mockData =
                    gridName === 'case-mgmt'
                        ? getMockData(filterGroup)
                        : getMockData(gridName ?? '')

                // Apply client-side filtering for mock data
                const systemKeys = ['limit', 'start', 'sort_order', 'sort_by', 'client_id', 'search', 'filter_group', 'cycle', 'is_fraud', 'primary_case_due_days']
                const filterEntries = Object.entries(payload).filter(
                    ([key, value]) => !systemKeys.includes(key) && !key.endsWith('_op') && value !== '' && value !== undefined && value !== null
                )

                let filteredData = mockData.data
                if (filterEntries.length && gridName !== 'case-mgmt') {
                    filteredData = mockData.data.filter((item: any) => {
                        return filterEntries.every(
                            ([key, value]: [string, any]) => {
                                const strValue = String(value)

                                // Date range filtering: value is "from|to" pipe-separated
                                if (strValue.includes('|')) {
                                    const [from, to] = strValue.split('|')
                                    const itemValue = item[key]
                                    if (
                                        itemValue === undefined ||
                                        itemValue === null
                                    )
                                        return false
                                    const itemStr = String(itemValue)
                                    if (from && itemStr < from) return false
                                    if (to && itemStr > to) return false
                                    return true
                                }

                                const itemValue = item[key]
                                if (
                                    itemValue === undefined ||
                                    itemValue === null
                                )
                                    return false

                                // Amount comparison filtering (operator in _op key)
                                const opKey = `${key}_op`
                                if (payload[opKey]) {
                                    const numItem = parseFloat(
                                        String(itemValue)
                                    )
                                    const numValue = parseFloat(strValue)
                                    switch (payload[opKey]) {
                                        case 'gt':
                                            return numItem > numValue
                                        case 'gte':
                                            return numItem >= numValue
                                        case 'lt':
                                            return numItem < numValue
                                        case 'lte':
                                            return numItem <= numValue
                                        case 'eq':
                                            return numItem === numValue
                                        default:
                                            return numItem === numValue
                                    }
                                }

                                // Default: case-insensitive substring match
                                return String(itemValue)
                                    .toLowerCase()
                                    .includes(strValue.toLowerCase())
                            }
                        )
                    })
                }

                // Apply client-side sorting for mock data
                const sortBy = payload.sort_by
                const sortOrder = payload.sort_order || 'asc'
                if (sortBy && filteredData.length) {
                    filteredData = [...filteredData].sort((a: any, b: any) => {
                        const valA = a[sortBy] ?? ''
                        const valB = b[sortBy] ?? ''
                        const strA = String(valA)
                        const strB = String(valB)
                        // Try numeric comparison
                        const numA = parseFloat(strA)
                        const numB = parseFloat(strB)
                        let cmp: number
                        if (!isNaN(numA) && !isNaN(numB)) {
                            cmp = numA - numB
                        } else {
                            cmp = strA.localeCompare(strB)
                        }
                        return sortOrder === 'desc' ? -cmp : cmp
                    })
                }

                const updatedMockData = filteredData.map(
                    (item: any, idx: number) => {
                        return {
                            ...item,
                            id: item.id ?? idx,
                            mockId: idx,
                        }
                    }
                )

                setData({ data: filteredData })
                setLocalCurrentPayload(payload)
                params.success({
                    rowData: updatedMockData,
                    rowCount: updatedMockData.length,
                })
                setPaginationTotal(updatedMockData.length)
                if (updatedMockData.length) {
                    gridRef.current!.api!.hideOverlay()
                }
                setIsLoading(false)
                return
            }

            axios
                .get(url, {
                    params: {
                        ...payload,
                    },
                })
                .then((response) => {
                    if (response.data.data) {
                        let { data: responseData, pagination } = updateData(
                            response.data,
                            gridName
                        )
                        if (
                            response.data.data.length &&
                            !response.data.data[0]?.id
                        ) {
                            responseData = response.data.data.map(
                                (
                                    value: { [key: string]: any },
                                    idx: number
                                ) => {
                                    return { rowIndex: idx, ...value }
                                }
                            )
                        }

                        const paginationTotal =
                            pagination?.total ?? responseData.length
                        setData(response.data)
                        setLocalCurrentPayload(payload)

                        params.success({
                            rowData: responseData,
                            rowCount: paginationTotal,
                        })
                        setPaginationTotal(paginationTotal)
                        if (paginationTotal) {
                            gridRef.current!.api!.hideOverlay()
                        }
                    } else {
                        let gridData = response.data

                        // If no 'id' property, then adding 'rowIndex' property to prevent errors with AG Grid's getRowId().
                        if (response.data.length && !response.data[0]?.id) {
                            gridData = response.data.map(
                                (
                                    value: { [key: string]: any },
                                    idx: number
                                ) => {
                                    return { rowIndex: idx, ...value }
                                }
                            )
                        }
                        setData(gridData)
                        setLocalCurrentPayload(payload)
                        params.success({
                            rowData: gridData,
                            rowCount: gridData.length,
                        })
                        setPaginationTotal(gridData.length)
                        if (gridData.length) {
                            gridRef.current!.api!.hideOverlay()
                        }
                    }
                })
                .catch(() => {
                    if (isMounted.current) {
                        // params.fail()
                        params.success({
                            rowData: [],
                            rowCount: 0,
                        })
                    }
                })
                .finally(() => {
                    if (isMounted.current) {
                        gridRef?.current!?.api?.refreshCells({
                            force: true,
                        })
                        setIsLoading(false)
                    }
                })
        },
    }

    const refreshAGGrid = () => {
        const api = gridRef?.current!?.api!
        if (api) {
            api.refreshServerSide()
            api.showLoadingOverlay()
        }
    }

    //Needed for Pagination
    useEffect(() => {
        pageSize.current = localPageSize
        localActiveNodeId.current = activeNodeId
        page.current =
            localActiveNodeId.current &&
            localActiveNodeId.current !== activeNodeId
                ? 1
                : localPage
        refreshAGGrid()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localPage, localPageSize])

    const handlePageLimitChange = (size: number) => {
        setLocalPageSize(size)
        setLocalPage(1)
    }

    //Needed for Search
    useEffect(() => {
        searchValue.current = localSearchValue
        localPage === 1 ? refreshAGGrid() : setLocalPage(1)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localSearchValue])

    const handleSearchField = (value: string) => {
        setLocalSearchValue(value)
    }

    //Needed for Payload Changes
    useEffect(() => {
        currentPayload.current = requestPayload
        localPage === 1 ? refreshAGGrid() : setLocalPage(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestPayload])

    const csvContextMenuItem = (fileName: string) => {
        return {
            name: 'CSV Export',
            action: () => {
                gridRef.current!.api.exportDataAsCsv({
                    fileName: fileName,
                    sheetName: fileName,
                    columnKeys: gridRef
                        .current!.api?.getColumnDefs()
                        .filter(
                            (col: any) =>
                                !excludedExportColumns.includes(col.colId) &&
                                !col.hide
                        )
                        .map((col: any) => col.colId),
                    ...(handleFormatExportCells && {
                        processCellCallback: handleFormatExportCells,
                    }),
                    ...(handleSkipExportRow && {
                        shouldRowBeSkipped: handleSkipExportRow,
                    }),
                })
            },
            icon: '<i class="fa fa-file-csv-ag-grid"/>',
        }
    }

    const excelContextMenuItem = (fileName: string) => {
        return {
            name: 'Excel Export',
            action: () => {
                gridRef.current!.api.exportDataAsExcel({
                    fileName: fileName,
                    sheetName: fileName,
                    columnKeys: gridRef
                        .current!.api?.getColumnDefs()
                        .filter(
                            (col: any) =>
                                !excludedExportColumns.includes(col.colId) &&
                                !col.hide
                        )
                        .map((col: any) => col.colId),
                    ...(handleFormatExportCells && {
                        processCellCallback: handleFormatExportCells,
                    }),
                    ...(handleSkipExportRow && {
                        shouldRowBeSkipped: handleSkipExportRow,
                    }),
                })
            },
            icon: '<i class="fa fa-file-excel-ag-grid"/>',
        }
    }

    const resetDataGridContextMenuItem = (gridName: string) => {
        return {
            name: 'Reset Columns',
            action: () => {
                gridRef.current!.columnApi.resetColumnState()
                gridRef.current!.api.setFilterModel(null)
                gridRef.current!.api.deselectAll(null)
                localStorage.removeItem(`col-${gridName}`)
                localStorage.removeItem(`filter-${gridName}`)
            },
        }
    }

    return {
        data,
        dataSource,
        isLoading,
        pageSize: pageSize.current,
        page: page.current,
        paginationTotal,
        setLocalPage,
        handlePageLimitChange,
        handleSearchField,
        currentPayload: localCurrentPayload,
        refreshAGGrid,
        csvContextMenuItem,
        excelContextMenuItem,
        resetDataGridContextMenuItem,
    }
}
